import React from 'react';

import CampaignLink from '../campaigns/CampaignLink';
import ListSelect from '../generic/ListSelect';
import { FILTER_TYPES } from '../generic/Table/utils';
import InfoTooltip from '../generic/Tooltip/InfoTooltip';
import { MerchantLinks } from '../merchants/Link';
import { UserLinks } from '../users/Link';
import UserLink from '../users/UserLink';
import { withFullName } from '../users/utils';
import {
  currencyRangeColumn,
  dateColumn,
  dateRangeColumn,
  filterMultiBy,
  filterSingleBy,
  filterStartsWith,
  simpleColumn,
} from '../utils/column-helpers';
import {
  brandLinks,
  ownerLink,
  salesTeamColumn,
  salesUnitColumn,
} from '../utils/columns';
import HELP_TEXTS from './help-texts';
import OpportunityLink from './OpportunityLink';
import StageBadge from './StageBadge';

export const number = {
  ...simpleColumn('Number', 'number', 110),
  sortKey: 'id',
};
export const name = simpleColumn('Name', 'name', 240);

export const salesUnit = salesUnitColumn('salesUnit');
export const salesTeam = salesTeamColumn('salesTeam');
export const salesChannel = simpleColumn('Sales Channel', 'salesChannel');

const businessModel = {
  Header: 'Bus. Model',
  accessor: 'businessModel',
  width: 85,
  filterMethod: (filter, value) =>
    filterStartsWith(filter, value.businessModel),
};

const campaignStart = dateRangeColumn('Start', 'campaignStart', 100, true);
const campaignEnd = dateRangeColumn('End', 'campaignEnd', 100, true);
const lastModified = dateColumn('Last Modified', 'lastModified', 100, false);

export const nameLink = {
  Header: 'Name',
  width: 240,
  accessor: 'name',
  Cell: row => <OpportunityLink model={row.original} />,
};

export const stage = {
  Header: 'Stage',
  accessor: 'stage',
  Cell: row => <StageBadge stage={row.value} />,
};

export const netClientBudget = currencyRangeColumn({
  Header: 'Net Client Budget',
  accessor: 'netClientBudget',
  id: 'netClientBudget',
  width: 150,
});

export const coInvest = currencyRangeColumn({
  Header: 'CoInvest',
  accessor: 'coInvest',
  id: 'coInvest',
});

export const freeService = currencyRangeColumn({
  Header: 'Free Service',
  accessor: 'freeService',
  id: 'freeService',
});

export const plannedBudget = currencyRangeColumn({
  Header: 'Budget',
  accessor: 'spendableBudget',
  id: 'spendableBudget',
});

export const expectedRevenue = {
  ...currencyRangeColumn({
    Header: 'Exp. Revenue',
    accessor: 'expectedRevenue',
    id: 'expectedRevenue',
  }),
  Header: () => (
    <>
      <span id="expected-revenue-header">Exp. Revenue</span>
      <span>
        <InfoTooltip target="opp-expected-revenue">
          {HELP_TEXTS.info.expectedRevenue}
        </InfoTooltip>
      </span>
    </>
  ),
  Label: 'Exp. Revenue',
  width: 130,
  // Temporarily turned off until BE changes for netClientBudget are not live
  filterable: false,
  sortable: false,
};

export const campaignLink = {
  Header: 'Campaign',
  accessor: 'campaignId',
  Cell: row =>
    row.value && (
      <CampaignLink model={{ id: row.value, name: `N${row.value}` }} />
    ),
  filterable: false,
  sortable: false,
};

export const merchantName = {
  Header: 'Merchants',
  id: 'merchants',
  accessor: 'merchants',
  width: 240,
  Cell: row => (row.value ? <MerchantLinks models={row.value} /> : null),
  Filter: props => <ListSelect {...props} type="allZmsamMerchant" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'merchantIds',
  sortable: false,
};

export const wkzBudget = currencyRangeColumn({
  Header: 'WKZ Budget',
  accessor: 'wkzBudget',
  id: 'wkzBudget',
  width: 100,
});

export const selfServiceCampaignStatus = simpleColumn(
  'Self Service Campaign Status',
  'selfServiceCampaignStatus',
  200,
);

export const selfServiceNCode = simpleColumn(
  'Self Service N-Code',
  'selfServiceNCode',
  200,
);

export const creativeTeam = simpleColumn('Creative Team', 'creativePool', 150);

export const PC1Link = {
  Header: 'PC 1',
  id: 'pc1',
  accessor: 'pc1',
  width: 200,
  filterMethod: (filter, value) =>
    value.pc1 && filterSingleBy(filter, value.pc1.id),
  Cell: row => row.value && <UserLink model={row.value} />,
  Filter: props => <ListSelect {...props} type="user" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'pc1__id',
  sortKey: 'pc1__firstName',
};

const collaborators = {
  Header: 'Collaborators',
  accessor: 'collaboratorIds',
  width: 200,
  filterMethod: (filter, value) => {
    const users = value.collaboratorIds.map(user => withFullName(user));
    return filterMultiBy(filter, users, 'fullName');
  },
  Cell: row => <UserLinks ids={row.value} />,
  Filter: props => <ListSelect {...props} type="user" />,
  filterCriteria: FILTER_TYPES.LIST_SELECT,
  filterKey: 'collaborators__id',
  sortable: false,
};

export const campaignType = simpleColumn('Campaign Type', 'campaignType');
export const useCase = simpleColumn('Use Case', 'useCase');
export const campaignObjective = simpleColumn(
  'Campaign Objective',
  'campaignObjective',
);
export const commercialPackage = simpleColumn(
  'Commercial Package',
  'commercialPackage.value',
);

export const commercialIncentive = simpleColumn(
  'Commercial Incentive',
  'primaryQuote.commercialIncentive',
);

export const spentBudget = currencyRangeColumn({
  Header: 'Spent',
  accessor: 'spent',
  id: 'spent',
  width: 100,
});

export const salesforceId = {
  ...simpleColumn('Salesforce ID', 'salesforceId', 140),
  filterable: true,
  sortable: false,
};

export const accountSfId = {
  ...simpleColumn('Account ID', 'accountSfId', 140),
  filterable: true,
  sortable: false,
};

export const reservationId = {
  ...simpleColumn('Reservation ID', 'reservationId', 140),
  filterable: true,
  sortable: false,
};

export const listColumns = [
  number,
  nameLink,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  expectedRevenue,
  campaignStart,
  campaignEnd,
  accountSfId,
  brandLinks,
  businessModel,
  salesUnit,
  salesTeam,
  salesChannel,
  ownerLink,
];

export const listColumnsAll = [
  number,
  nameLink,
  stage,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  expectedRevenue,
  campaignStart,
  campaignEnd,
  accountSfId,
  brandLinks,
  businessModel,
  salesUnit,
  salesTeam,
  salesChannel,
  ownerLink,
  lastModified,
];

export const selectColumns = [
  number,
  name,
  stage,
  expectedRevenue,
  salesUnit,
  salesTeam,
  ownerLink,
  accountSfId,
  brandLinks,
];

export const defaultListColumns = [
  salesforceId,
  reservationId,
  number,
  nameLink,
  stage,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  expectedRevenue,
  campaignStart,
  campaignEnd,
  accountSfId,
  brandLinks,
  businessModel,
  salesUnit,
  salesTeam,
  salesChannel,
  ownerLink,
  campaignLink,
  merchantName,
];

export const allColumns = [
  number,
  nameLink,
  stage,
  netClientBudget,
  coInvest,
  freeService,
  plannedBudget,
  spentBudget,
  expectedRevenue,
  campaignStart,
  campaignEnd,
  accountSfId,
  brandLinks,
  businessModel,
  salesUnit,
  salesTeam,
  salesChannel,
  ownerLink,
  campaignLink,
  merchantName,
  salesforceId,
  reservationId,
  wkzBudget,
  selfServiceCampaignStatus,
  selfServiceNCode,
  creativeTeam,
  PC1Link,
  collaborators,
  campaignType,
  useCase,
  campaignObjective,
  commercialPackage,
  commercialIncentive,
];
